import type { AvatarCatalog, AvatarShape } from '@/shared/features/user-avatar';

import { assetsUrl } from '../../../consts/env';

import type { AvatarDecorateValue } from './schema';

/**
 * 코어 `DecorateAvatarModal` 의 파츠 선택 로직을 quiz 전용으로 포팅.
 * (avatarShapeVariants.ts + avatarAccessoryTabs.ts + AvatarContainer 의 매핑)
 */

export type CategoryTab = 'face' | 'skin' | 'hair' | 'clothes' | 'glasses';

type BaseField = 'skinId' | 'faceId' | 'hairId' | 'clothesId';

interface CategoryConfig {
  tab: CategoryTab;
  /** 카탈로그 catalog.shapes 의 키 */
  shapeKey: string;
  /** 일반 파츠면 avatar 의 필드명, 액세서리면 accessorySlot */
  field?: BaseField;
  accessorySlot?: string;
}

/** 코어 AvatarCategoryTabs 순서와 동일 (NFT 제외). 액세서리는 glasses 만 노출. */
export const CATEGORY_CONFIG: readonly CategoryConfig[] = [
  { tab: 'face', shapeKey: 'face', field: 'faceId' },
  { tab: 'skin', shapeKey: 'skin', field: 'skinId' },
  { tab: 'hair', shapeKey: 'hair', field: 'hairId' },
  { tab: 'clothes', shapeKey: 'clothes', field: 'clothesId' },
  { tab: 'glasses', shapeKey: 'glasses', accessorySlot: 'glasses' },
];

export function getCategoryConfig(tab: CategoryTab): CategoryConfig {
  return CATEGORY_CONFIG.find(c => c.tab === tab) ?? CATEGORY_CONFIG[0];
}

/** 현재 탭에서 avatar 값으로부터 선택된 partId 를 읽는다. */
export function getSelectedPartId(
  avatar: AvatarDecorateValue,
  tab: CategoryTab,
): number {
  const cfg = getCategoryConfig(tab);
  if (cfg.accessorySlot) {
    return Number(avatar.accessoryIds?.[cfg.accessorySlot] ?? 0);
  }
  return Number(avatar[cfg.field as BaseField] || 0);
}

/** 일반 파츠 선택 결과를 avatar 에 반영한 새 객체 반환. */
export function applyPartSelection(
  avatar: AvatarDecorateValue,
  tab: CategoryTab,
  partId: number,
): AvatarDecorateValue {
  const cfg = getCategoryConfig(tab);
  if (cfg.accessorySlot) {
    return {
      ...avatar,
      accessoryIds: setAccessoryId(
        avatar.accessoryIds,
        cfg.accessorySlot,
        partId,
      ),
    };
  }
  return { ...avatar, [cfg.field as BaseField]: partId };
}

// --- accessory helpers (avatarAccessoryTabs.ts 포팅) ---

export function setAccessoryId(
  accessoryIds: Record<string, number> | null | undefined,
  slot: string,
  id: number,
): Record<string, number> {
  return {
    ...Object.fromEntries(
      Object.entries(accessoryIds ?? {}).filter(
        ([, v]) => typeof v === 'number' && Number.isFinite(v),
      ),
    ),
    [slot]: id,
  };
}

/** 같은 액세서리를 다시 고르면 해제(0), 아니면 교체. */
export function getNextAccessoryId(currentId: number, selectedId: number) {
  return Math.abs(currentId) === Math.abs(selectedId) ? 0 : selectedId;
}

// --- shape variant helpers (avatarShapeVariants.ts 포팅) ---

export function shouldShowColorPicker(
  selectedShape: AvatarShape | null,
): boolean {
  return selectedShape !== null && selectedShape.parts.length > 1;
}

export function getVariantPartIdForShape(
  shape: AvatarShape,
  selectedColorIndex: number,
): { partId: number; colorIndex: number } | null {
  if (shape.parts.length === 0) return null;
  const colorIndex = Math.min(
    Math.max(selectedColorIndex, 0),
    shape.parts.length - 1,
  );
  const part = shape.parts[colorIndex];
  if (!part) return null;
  return { partId: -part.partId, colorIndex };
}

/** 코어 @zep/apis getPartHex 1:1 포팅 — 카테고리에서 partId 의 색(hex) 반환. */
export function getPartHex(
  catalog: AvatarCatalog,
  categoryKey: string,
  partId: number,
): string | null {
  const shapes = catalog.shapes[categoryKey] ?? [];
  for (const shape of shapes) {
    const part = shape.parts.find(p => p.partId === Math.abs(partId));
    if (part?.hex) return part.hex;
  }
  return null;
}

export function findShapeByPartId(
  shapes: AvatarShape[],
  partId: number,
): { shape: AvatarShape; colorIndex: number } | null {
  const absId = Math.abs(partId);
  for (const shape of shapes) {
    const idx = shape.parts.findIndex(p => p.partId === absId);
    if (idx !== -1) return { shape, colorIndex: idx };
  }
  return null;
}

/** 그리드 썸네일 아이템 (코어 selectedCategoryItems 와 동일 형태). */
export interface PartGridItem {
  id: number;
  shapeId: number;
  pathName: string;
  isNew: boolean;
  isHot: boolean;
  isLocked: boolean;
  isOwned: boolean;
  price?: number;
  isFree: boolean;
}

function buildAvatarAssetUrl(path: string) {
  const normalizedAssetsUrl = assetsUrl.trim().replace(/\/+$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  return normalizedAssetsUrl
    ? `${normalizedAssetsUrl}${normalizedPath}`
    : normalizedPath;
}

export function buildGridItems(
  shapes: AvatarShape[],
  shapeKey: string,
): PartGridItem[] {
  return shapes.map(shape => {
    const previewPart = shape.parts.find(p => p.partId === shape.previewPartId);
    const versionSuffix = previewPart?.imageVersion
      ? `?v=${previewPart.imageVersion}`
      : '';
    return {
      id: -shape.previewPartId,
      shapeId: shape.id,
      pathName: `${buildAvatarAssetUrl(
        `/static/assets/sprites/${shapeKey}/-${shape.previewPartId}_preview.png`,
      )}${versionSuffix}`,
      isNew: shape.isNew,
      isHot: shape.isHot,
      isLocked: shape.usable === false,
      isOwned: shape.owned ?? false,
      price: shape.price,
      isFree: shape.isFree ?? true,
    };
  });
}

/**
 * 아이템 목록 정렬 기준(Figma 1506:33351 의 Select 옵션).
 * - `default`: 추천순. 서버가 준 순서 그대로가 곧 추천 순서다(기본값).
 * - `latest`: 최신순. 출시일 필드(`saleStartedAt`)가 전부 비어 있어 shapeId 내림차순을
 *   프록시로 쓴다. `isNew` 가 붙은 아이템이 전부 최상위 id 에 몰려 있어 실측상 일치한다.
 * - `priceAsc`: 낮은 가격순. 무료(0)가 먼저 온다.
 * - `priceDesc`: 높은 가격순. 무료(0)가 마지막에 온다.
 */
export type PartSortKey = 'default' | 'latest' | 'priceAsc' | 'priceDesc';

/**
 * 그리드 아이템을 정렬해 새 배열로 돌려준다(원본 불변).
 *
 * `Array.prototype.sort` 는 안정 정렬이라 비교값이 같으면 서버 순서가 유지된다.
 */
export function sortGridItems(
  items: PartGridItem[],
  sortKey: PartSortKey,
): PartGridItem[] {
  if (sortKey === 'default') return items;

  const sorted = [...items];
  switch (sortKey) {
    case 'latest':
      return sorted.sort((a, b) => b.shapeId - a.shapeId);
    case 'priceAsc':
      return sorted.sort((a, b) => (a.price ?? 0) - (b.price ?? 0));
    case 'priceDesc':
      return sorted.sort((a, b) => (b.price ?? 0) - (a.price ?? 0));
  }
}

/**
 * 구매하지 않은 유료 아이템을 착용 중이면 무료 아이템으로 되돌린다.
 *
 * 유료화 이전에 저장된 아바타가 지금은 잠긴(usable=false) 아이템을 가리키는 경우가 있어
 * "안 산 아이템을 입고 있는" 상태로 보인다. 기획상 미구매 슬롯은 무료 아이템이어야 한다.
 *
 * - 필수 슬롯(피부/얼굴/헤어/의상): 첫 무료 아이템으로 교체
 * - 액세서리(안경): 해제(0). 고르지도 않은 무료 액세서리를 강제로 씌우지 않는다.
 *
 * 바뀐 게 없으면 원본 객체를 그대로 돌려준다(불필요한 리렌더·dirty 방지).
 */
export function sanitizeUnownedParts(
  avatar: AvatarDecorateValue,
  catalog: AvatarCatalog,
): AvatarDecorateValue {
  let next = avatar;

  for (const cfg of CATEGORY_CONFIG) {
    const currentId = getSelectedPartId(next, cfg.tab);
    if (!currentId) continue;

    const shapes = catalog.shapes[cfg.shapeKey] ?? [];
    const worn = findShapeByPartId(shapes, currentId);
    if (!worn || worn.shape.usable !== false) continue;

    if (cfg.accessorySlot) {
      next = applyPartSelection(next, cfg.tab, 0);
      continue;
    }

    const free = shapes.find(s => s.usable !== false && (s.isFree ?? true));
    if (!free) continue;
    next = applyPartSelection(next, cfg.tab, -free.previewPartId);
  }

  return next;
}

/** 랜덤 아바타: 카테고리별 전체 partId 중 무작위(음수 컨벤션). 코어 pickRandom 과 동일. */
export function pickRandomPartId(shapes: AvatarShape[]): number {
  // 구매했거나 무료인 것(usable)만 뽑는다. 잠긴 유료 아이템이 걸리면
  // 사지도 않은 아이템을 입은 상태가 되고, sanitizeUnownedParts 가 다시 되돌린다.
  const usableShapes = shapes.filter(s => s.usable !== false);
  const partIds = usableShapes.flatMap(s => s.parts.map(p => p.partId));
  if (partIds.length === 0) return -1;
  return -partIds[Math.floor(Math.random() * partIds.length)];
}

/**
 * 액세서리 슬롯용 랜덤 뽑기. 보유(usable) 기준은 pickRandomPartId 와 같고 두 가지가 다르다:
 * - 미착용(0)이 유효한 상태다. 필수 슬롯과 달리 비어 있어도 된다.
 * - '없음' 도 결과 후보에 넣는다. 매번 액세서리를 씌우면 셔플이 한쪽으로 치우쳐
 *   벗은 모습으로는 영영 못 돌아간다.
 */
export function pickRandomAccessoryPartId(shapes: AvatarShape[]): number {
  const usableShapes = shapes.filter(s => s.usable !== false);
  const partIds = usableShapes.flatMap(s => s.parts.map(p => p.partId));
  if (partIds.length === 0) return 0;

  const index = Math.floor(Math.random() * (partIds.length + 1));
  // 마지막 칸이 '없음'
  return index === partIds.length ? 0 : -partIds[index];
}

/** 랜덤 꾸미기가 다룰 액세서리 슬롯. 카테고리 탭에 노출된 것과 항상 같이 간다. */
export function getAccessorySlots(): { slot: string; shapeKey: string }[] {
  return CATEGORY_CONFIG.filter(c => c.accessorySlot).map(c => ({
    slot: c.accessorySlot as string,
    shapeKey: c.shapeKey,
  }));
}
